import { httpGet } from "../http";
import { customerEndpoints } from "../endpoints";
import { DebtsService } from "./debtsService";
import type {
  CustomerDebtRecord,
  CustomerSharingResponse,
  CustomerSharingUpsertRequest,
  FreePostDataResponse,
  FreePostSharingPayload,
  ServiceResponse,
  ApiResponse,
} from "../types";

type DigitalLender = {
  lender: string;
  lenderName?: string; // some lists may use lenderName
  schemaName?: string | null;
  subDomain?: string | null;
  parentCompany?: string | null;
  description?: string | null; // child company label
};

type FreepostLender = {
  companyName?: string | null;
  creditor?: string | null;
  lender?: string | null;
  parentCompany?: string | null;
  description?: string | null;
  creditorAddress?: string | null;
};

export interface SharingLookups {
  digitalMap: Map<string, DigitalLender>;
  freepostSet: Set<string>;
  freepostMap: Map<string, FreepostLender>;
}

export interface ExistingSharingMaps {
  digitalByDebtId: Map<number, CustomerSharingResponse>;
  digitalByKey: Map<string, CustomerSharingResponse>;
  /**
   * Sharing rows that have no usable debtId linkage.
   *
   * Some legacy / referral flows create digital share rows without `debtId`.
   * When the debt reference number changes, we can't match by (lenderName|accountNumber)
   * and we also can't match by debtId — which can lead to a POST that creates duplicates.
   *
   * This map allows us to fall back to updating the existing row by its `id`.
   */
  digitalOrphansByLender: Map<string, CustomerSharingResponse>;
  freepostByDebtId: Map<number, FreePostDataResponse>;
  freepostByKey: Map<string, FreePostDataResponse>;
}

// Simple in-module cache for lender lookups
let cachedLookupsValue: SharingLookups | null = null;
let cachedLookupsAt: number | null = null;
const LOOKUPS_TTL_MS = 15 * 60 * 1000; // 15 minutes

function normalize(value: unknown): string {
  return typeof value === "string"
    ? value.replace(/\s+/g, "").toLowerCase()
    : "";
}

function coerceNumericId(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function hasBeenShared(row: unknown): boolean {
  if (!row || typeof row !== "object") {
    return false;
  }
  const r = row as Record<string, unknown>;
  const submitDate = r["submitDate"];
  const lastSharedDate = r["lastSharedDate"];
  const isNonEmptyString = (v: unknown): v is string =>
    typeof v === "string" && v.trim().length > 0;
  return isNonEmptyString(submitDate) || isNonEmptyString(lastSharedDate);
}

function buildDigitalKey(
  lenderName: string,
  accountNumber: string | null | undefined
): string {
  return `${normalize(lenderName)}|${normalize(accountNumber ?? "")}`;
}

function buildFreepostKey(
  companyName: string,
  referenceNumber: string | null | undefined
): string {
    return `${normalize(companyName)}|${normalize(referenceNumber ?? "")}`;
}

// Debug logger for reasons why we do NOT add a record to the Share List APIs.
// Intentionally lightweight and safe in production; logs only on skip paths.
function logSkipSharing(
  reason: string,
  meta?: Record<string, unknown>
): void {
  try {
    // eslint-disable-next-line no-console
    console.info("[SharingOrchestrator] skip-add-share", {
      reason,
      ...(meta || {}),
    });
  } catch {
    // no-op: never throw from logging
  }
}

const debugEnabled =
  typeof process !== "undefined" && process?.env?.NODE_ENV !== "production";
function debugLog(message: string, meta?: Record<string, unknown>): void {
  if (!debugEnabled) return;
  try {
    // eslint-disable-next-line no-console
    console.info("[SharingOrchestrator]", message, meta || {});
  } catch {
    // never throw from logging
  }
}

export const SharingOrchestrator = {
  async fetchLookups(options?: {
    forceRefresh?: boolean;
  }): Promise<SharingLookups> {
    const now = Date.now();
    if (
      !options?.forceRefresh &&
      cachedLookupsValue &&
      cachedLookupsAt &&
      now - cachedLookupsAt < LOOKUPS_TTL_MS
    ) {
      return cachedLookupsValue;
    }
    const [digitalResp, freepostResp] = await Promise.all([
      httpGet<ApiResponse<DigitalLender[]>>(
        customerEndpoints.listDigitalLenders
      ),
      httpGet<ApiResponse<FreepostLender[]>>(
        customerEndpoints.listFreepostLenders
      ),
    ]);

    const digital: DigitalLender[] = Array.isArray(digitalResp?.data)
      ? digitalResp.data
      : [];
    const freepost: FreepostLender[] = Array.isArray(freepostResp?.data)
      ? freepostResp.data
      : [];

    const digitalMap = new Map<string, DigitalLender>();
    digital.forEach((item) => {
      const keyCandidates = new Set<string>();
      const nameA = item.lender ?? item.lenderName ?? "";
      if (nameA) keyCandidates.add(normalize(nameA));
      if (item.parentCompany) keyCandidates.add(normalize(item.parentCompany));
      if (item.description) keyCandidates.add(normalize(item.description));
      keyCandidates.forEach((k) => {
        if (!digitalMap.has(k)) {
          digitalMap.set(k, item);
        }
      });
    });

    const freepostSet = new Set<string>();
    const freepostMap = new Map<string, FreepostLender>();
    freepost.forEach((f) => {
      // Accept multiple alias fields from API payloads for robust matching
      const candidates = [
        f?.companyName,
        (f as unknown as { creditor?: string | null })?.creditor,
        (f as unknown as { lender?: string | null })?.lender,
        f?.parentCompany,
        f?.description,
      ];
      candidates.forEach((name) => {
        const norm = normalize(name ?? "");
        if (norm) {
          freepostSet.add(norm);
          if (!freepostMap.has(norm)) {
            freepostMap.set(norm, f);
          }
        }
      });
    });

    const lookups = { digitalMap, freepostSet, freepostMap };
    cachedLookupsValue = lookups;
    cachedLookupsAt = Date.now();
    return lookups;
  },

  async fetchExisting(customerId: number): Promise<ExistingSharingMaps> {
    const [digitalListResp, freepostListResp, debtsResp] = await Promise.all([
      DebtsService.getCustomerSharing(customerId, { skipErrorCapture: true }),
      DebtsService.getFreePostSharing(customerId, { skipErrorCapture: true }),
      DebtsService.getDebts(customerId, { skipErrorCapture: true }),
    ]);

    const digitalList = Array.isArray(digitalListResp?.data)
      ? digitalListResp.data
      : [];
    const freepostList = Array.isArray(freepostListResp?.data)
      ? freepostListResp.data
      : [];
    const debtsList = Array.isArray(debtsResp?.data) ? debtsResp.data : [];

    // Build a set of valid debt IDs to filter out orphaned sharing records
    const validDebtIds = new Set<number>();
    debtsList.forEach((debt) => {
      const id = coerceNumericId((debt as unknown as { id?: unknown })?.id);
      if (typeof id === "number") validDebtIds.add(id);
    });

    const digitalByDebtId = new Map<number, CustomerSharingResponse>();
    const digitalByKey = new Map<string, CustomerSharingResponse>();
    const digitalOrphansByLender = new Map<string, CustomerSharingResponse>();
    digitalList.forEach((row) => {
      const debtId = coerceNumericId((row as unknown as { debtId?: unknown })?.debtId);
      // Only include sharing records that reference existing debts (filter out orphaned records)
      if (typeof debtId === "number" && validDebtIds.has(debtId)) {
        const prev = digitalByDebtId.get(debtId);
        const prevTs = prev?.lastUpdateDate ? Date.parse(prev.lastUpdateDate) : NaN;
        const nextTs = row?.lastUpdateDate ? Date.parse(row.lastUpdateDate) : NaN;
        // Prefer the most recently updated row when multiple exist for the same debtId.
        if (!prev || (Number.isFinite(nextTs) && (!Number.isFinite(prevTs) || nextTs >= prevTs))) {
          digitalByDebtId.set(debtId, row);
        }
      }
      // Add to byKey map if:
      // 1. The debt exists (normal case), OR
      // 2. The record is already shared (to prevent creating duplicates for orphaned but shared records)
      const debtExists = typeof debtId === "number" && validDebtIds.has(debtId);
      const alreadyShared = hasBeenShared(row);
      if (debtExists || alreadyShared) {
        const key = buildDigitalKey(row.lenderName, row.accountNumber);
        // If multiple records exist for the same key, prefer already-shared ones,
        // then prefer the most recently updated
        const existingByKey = digitalByKey.get(key);
        const existingShared = existingByKey ? hasBeenShared(existingByKey) : false;
        const existingTs = existingByKey?.lastUpdateDate ? Date.parse(existingByKey.lastUpdateDate) : NaN;
        const currentTs = row?.lastUpdateDate ? Date.parse(row.lastUpdateDate) : NaN;
        // Prefer already-shared records, or if both have same shared status, prefer more recent
        if (
          !existingByKey ||
          (alreadyShared && !existingShared) ||
          (alreadyShared === existingShared && Number.isFinite(currentTs) && (!Number.isFinite(existingTs) || currentTs >= existingTs))
        ) {
          digitalByKey.set(key, row);
        }
      }

      // Track orphaned rows (missing/unusable debtId) by lenderName so we can
      // update them instead of creating duplicates when the reference changes.
      if (debtId === null) {
        const lenderKey = normalize(row.lenderName);
        if (lenderKey) {
          const prev = digitalOrphansByLender.get(lenderKey);
          const prevTs = prev?.lastUpdateDate ? Date.parse(prev.lastUpdateDate) : NaN;
          const nextTs = row?.lastUpdateDate ? Date.parse(row.lastUpdateDate) : NaN;
          if (!prev || (Number.isFinite(nextTs) && (!Number.isFinite(prevTs) || nextTs >= prevTs))) {
            digitalOrphansByLender.set(lenderKey, row);
          }
        }
      }
    });

    const freepostByDebtId = new Map<number, FreePostDataResponse>();
    const freepostByKey = new Map<string, FreePostDataResponse>();
    freepostList.forEach((row) => {
      const debtId = coerceNumericId((row as unknown as { debtId?: unknown })?.debtId);
      // Only include sharing records that reference existing debts (filter out orphaned records)
      if (typeof debtId === "number" && validDebtIds.has(debtId)) {
        const prev = freepostByDebtId.get(debtId);
        const prevTs = prev?.lastUpdateDate ? Date.parse(prev.lastUpdateDate) : NaN;
        const nextTs = row?.lastUpdateDate ? Date.parse(row.lastUpdateDate) : NaN;
        if (!prev || (Number.isFinite(nextTs) && (!Number.isFinite(prevTs) || nextTs >= prevTs))) {
          freepostByDebtId.set(debtId, row);
        }
      }
      // Add to byKey map if:
      // 1. The debt exists (normal case), OR
      // 2. The record is already shared (to prevent creating duplicates for orphaned but shared records)
      const debtExists = typeof debtId === "number" && validDebtIds.has(debtId);
      const alreadyShared = hasBeenShared(row);
      if (debtExists || alreadyShared) {
        const key = buildFreepostKey(
          (row as unknown as { companyName?: string })?.companyName ?? "",
          row.referenceNumber
        );
        // If multiple records exist for the same key, prefer already-shared ones,
        // then prefer the most recently updated
        const existingByKey = freepostByKey.get(key);
        const existingShared = existingByKey ? hasBeenShared(existingByKey) : false;
        const existingTs = existingByKey?.lastUpdateDate ? Date.parse(existingByKey.lastUpdateDate) : NaN;
        const currentTs = row?.lastUpdateDate ? Date.parse(row.lastUpdateDate) : NaN;
        // Prefer already-shared records, or if both have same shared status, prefer more recent
        if (
          !existingByKey ||
          (alreadyShared && !existingShared) ||
          (alreadyShared === existingShared && Number.isFinite(currentTs) && (!Number.isFinite(existingTs) || currentTs >= existingTs))
        ) {
          freepostByKey.set(key, row);
        }
      }
    });

    return { digitalByDebtId, digitalByKey, digitalOrphansByLender, freepostByDebtId, freepostByKey };
  },

  classifyDebt(
    debt: CustomerDebtRecord,
    lookups: SharingLookups
  ): { kind: "digital" | "freepost" | "none"; lender?: DigitalLender } {
    const creditorNorm = normalize(debt?.lenderName ?? debt?.creditor ?? "");
    if (!creditorNorm) return { kind: "none" };

    const digitalMatch = lookups.digitalMap.get(creditorNorm);
    if (digitalMatch) {
      return { kind: "digital", lender: digitalMatch };
    }

    if (lookups.freepostSet.has(creditorNorm)) {
      return { kind: "freepost" };
    }

    return { kind: "none" };
  },

  async upsertForDebt(
    customerId: number,
    debt: CustomerDebtRecord,
    lookups: SharingLookups,
    existing: ExistingSharingMaps,
    options?: { freepostAddress?: string | null; freepostNotes?: string | null }
  ): Promise<ServiceResponse<unknown>> {
    // Guardrails
    const accTypeCode =
      (debt as unknown as { accTypeCode?: string | null })?.accTypeCode ?? null;
    if (accTypeCode === "FC") {
      logSkipSharing("FC debts are excluded from sharing", {
        customerId,
        debtId: (debt as { id?: unknown })?.id,
        accTypeCode,
      });
      return {
        success: true,
        data: null,
        message: "Skipped FC debt",
        timestamp: new Date().toISOString(),
      };
    }

    const accountNumber = (debt.accountNumber ?? "").trim();
    const creditorName = (debt.lenderName ?? debt.creditor ?? "").trim();
    const creditorNorm = normalize(creditorName);
    if (!creditorName || !accountNumber) {
      logSkipSharing("Missing creditor or accountNumber", {
        customerId,
        debtId: (debt as { id?: unknown })?.id,
        creditorNamePresent: Boolean(creditorName),
        accountNumberPresent: Boolean(accountNumber),
      });
      return {
        success: true,
        data: null,
        message: "Skipped debt without creditor/accountNumber",
        timestamp: new Date().toISOString(),
      };
    }

    // Verify debt actually exists before creating sharing records
    // (debt might have been deleted, causing "already exists" errors)
    try {
      const debtsResp = await DebtsService.getDebts(customerId, {
        skipErrorCapture: true,
      });
      const debtsList = Array.isArray(debtsResp?.data) ? debtsResp.data : [];
      const debtExists = debtsList.some((d) => d.id === debt.id);
      if (!debtExists && typeof debt.id === "number") {
        logSkipSharing("Debt no longer exists (deleted), skipping sharing", {
          customerId,
          debtId: debt.id,
        });
        return {
          success: true,
          data: null,
          message: `Debt ${debt.id} was deleted, skipping sharing`,
          timestamp: new Date().toISOString(),
        };
      }
    } catch {
      // Continue anyway - worst case we'll get an error
    }

    const debtSourceRaw = (debt as unknown as { source?: string | null })?.source;
    const debtSource =
      typeof debtSourceRaw === "string" ? debtSourceRaw : debtSourceRaw ?? null;
    const classification = this.classifyDebt(debt, lookups);
    debugLog("classification", {
      creditorName,
      accountNumber,
      kind: classification.kind,
      debtId: (debt as { id?: unknown })?.id,
      source: debtSource ?? null,
    });
    // Do NOT auto-force Freepost for arrears (source = customer_expenses) when the
    // creditor is not recognised in either the digital or freepost catalogues.
    // Only recognised digital/freepost creditors should be added to the Share page.
    const effectiveKind = classification.kind;

    if (effectiveKind === "none") {
      debugLog("skip: no sharing target (not digital/freepost)", {
        creditorName,
        accountNumber,
        debtId: (debt as { id?: unknown })?.id,
      });
      logSkipSharing("Unrecognised creditor (not digital or freepost)", {
        customerId,
        debtId: (debt as { id?: unknown })?.id,
        creditorName,
        accountNumber,
        source: debtSource ?? null,
      });
      return {
        success: true,
        data: null,
        message: "No sharing target",
        timestamp: new Date().toISOString(),
      };
    }

    // Treat debts created from the customer profile (referral flows) specially in sharing.
    // Backwards compatible: some legacy rows use "customer_profile" and newer ones
    // may use "referred" directly as the source marker.
    const isProfileManagedDebt =
      debtSource === "customer_profile" || debtSource === "referred";

    if (effectiveKind === "digital") {
      const lenderName =
        classification.lender?.lender ??
        classification.lender?.lenderName ??
        creditorName;
      const schemaName = classification.lender?.schemaName ?? null;
      const subDomain = classification.lender?.subDomain ?? null;

      const payload: CustomerSharingUpsertRequest = {
        customerId,
        debtId: debt.id,
        lenderName,
        accountNumber,
        ...(schemaName ? { schemaName } : {}),
        ...(subDomain ? { subDomain } : {}),
        // Legacy parity:
        // - Profile-managed (referred) debts default to shareNow "Y" (profile debt sets shareBudget: 'Y').
        // - Normal debts default to "N" so the Share page controls explicit consent.
        shareNow: isProfileManagedDebt ? "Y" : "N",
        // When the originating debt came from a referral/company on the profile page,
        // mark the sharing entry so downstream systems can distinguish it.
        // else set as debt so it can be categorised properly in share page
        ...(isProfileManagedDebt
          ? { source: "referred" }
          : { source: "debts" }),
        isSharing: "Y",
      };

      // Resolve update vs create
      // Only update if we find a record by debtId AND the existing record's debtId matches the new debt's id
      // This prevents updating orphaned records that might reference deleted debts
      const byId = existing.digitalByDebtId.get(debt.id);
      const existingDebtId = byId
        ? coerceNumericId((byId as unknown as { debtId?: unknown })?.debtId)
        : null;

      // FIRST CHECK: If the existing share record has already been shared, do NOT touch it.
      // Once a share record has submitDate or lastSharedDate, it is immutable from debt updates.
      // Neither update nor create a new row - just skip entirely.
      if (byId && hasBeenShared(byId)) {
        return {
          success: true,
          data: null,
          message: "Sharing record already shared, skipping",
          timestamp: new Date().toISOString(),
        };
      }

      if (
        byId &&
        typeof (byId as { id?: number }).id === "number" &&
        existingDebtId === debt.id
      ) {
        // Record exists but hasn't been shared yet
        // Check if company (lenderName) or reference (accountNumber) changed
        const existingLenderName = (byId as CustomerSharingResponse).lenderName ?? "";
        const existingAccountNumber = (byId as CustomerSharingResponse).accountNumber ?? "";
        const lenderChanged = normalize(existingLenderName) !== normalize(lenderName);
        const referenceChanged = normalize(existingAccountNumber) !== normalize(accountNumber);

        if (lenderChanged || referenceChanged) {
          // Company or reference changed - fall through to create a new share record
          // (the old unshared record will remain orphaned or be cleaned up separately)
        } else {
          // Company and reference same - safe to update with PATCH
          return DebtsService.updateCustomerSharing(
            { ...payload, id: (byId as { id: number }).id },
            { skipErrorCapture: true }
          );
        }
      }

      // Fallback for profile-managed (referral) debts:
      // If the existing share row has no debtId (legacy API rows), we can't match by debtId
      // and when the accountNumber changes we also can't match by key. Update the orphan row
      // by its share `id` instead of creating a duplicate share entry.
      if (!byId && isProfileManagedDebt) {
        const orphan = existing.digitalOrphansByLender.get(normalize(lenderName));
        if (orphan && typeof (orphan as { id?: number }).id === "number") {
          // If orphan is already shared, skip entirely - don't touch shared records
          if (hasBeenShared(orphan)) {
            return {
              success: true,
              data: null,
              message: "Orphan sharing record already shared, skipping update",
              timestamp: new Date().toISOString(),
            };
          }
          // Check if company or reference changed
          const orphanLenderName = (orphan as CustomerSharingResponse).lenderName ?? "";
          const orphanAccountNumber = (orphan as CustomerSharingResponse).accountNumber ?? "";
          const lenderChanged = normalize(orphanLenderName) !== normalize(lenderName);
          const referenceChanged = normalize(orphanAccountNumber) !== normalize(accountNumber);

          if (lenderChanged || referenceChanged) {
            // Company or reference changed - fall through to create a new share record
          } else {
            // Company and reference same - safe to update with PATCH
            return DebtsService.updateCustomerSharing(
              { ...payload, id: (orphan as { id: number }).id },
              { skipErrorCapture: true }
            );
          }
        }
      }

      // Check if sharing record exists by key (creditor/account number) but debtId doesn't match
      // For non profile-managed debts this usually means another debt already owns
      // this lender/accountNumber combination and we should not create a duplicate.
      // For profile-managed debts (referred company flows), we intentionally allow
      // multiple sharing rows for the same lender/accountNumber so that updating the
      // referred company can create a new share entry without mutating any existing one.
      const byKey = existing.digitalByKey.get(
        buildDigitalKey(lenderName, accountNumber)
      );
      if (byKey) {
        // If the existing record by key is already shared, skip - don't link new debt to shared record
        if (hasBeenShared(byKey)) {
          return {
            success: true,
            data: null,
            message: "Sharing record already shared (by key), skipping",
            timestamp: new Date().toISOString(),
          };
        }
        const keyDebtId = (byKey as unknown as { debtId?: number })?.debtId;
        if (!isProfileManagedDebt && keyDebtId !== debt.id) {
          logSkipSharing(
            "Digital sharing exists for another debt (duplicate key), skipping",
            {
              customerId,
              debtId: (debt as { id?: unknown })?.id,
              existingDebtId: keyDebtId ?? null,
              lenderName,
              accountNumber,
              profileManaged: isProfileManagedDebt,
            }
          );
          return {
            success: true,
            data: null,
            message: "Sharing record exists for different debt, skipping",
            timestamp: new Date().toISOString(),
          };
        }
      }

      // If no record found by debtId or debtId doesn't match, create a new one (even if byKey exists, as it might be orphaned)
      try {
        return await DebtsService.createCustomerSharing(payload, {
          skipErrorCapture: true,
        });
      } catch (createError: any) {
        const errorMessage =
          createError?.response?.data?.errorMessage ??
          createError?.message ??
          "";
        if (errorMessage.includes("already exists")) {
          logSkipSharing("Digital sharing already exists (API says exists)", {
            customerId,
            debtId: (debt as { id?: unknown })?.id,
            lenderName,
            accountNumber,
          });
          return {
            success: true,
            data: null,
            message: "Sharing record already exists, skipping",
            timestamp: new Date().toISOString(),
          };
        }
        throw createError;
      }
    }

    // Freepost
    const freepostRecord = lookups.freepostMap?.get(creditorNorm);
    const freepostAddress =
      options?.freepostAddress ?? freepostRecord?.creditorAddress ?? null;
    const freepostNotes = options?.freepostNotes ?? null;

    const freepostPayload: FreePostSharingPayload = {
      customerId,
      debtId: debt.id,
      companyName: creditorName,
      referenceNumber: accountNumber,
      // Legacy parity: same defaulting as digital sharing (see above).
      // Profile-managed (referred) debts default to shareNow "Y"; normal debts default to "N".
      shareNow: isProfileManagedDebt ? "Y" : "N",
      ...(isProfileManagedDebt ? { source: "referred" } : {}),
      ...(freepostAddress ? { address: freepostAddress } : {}),
      ...(freepostNotes ? { notes: freepostNotes } : {}),
    };
    debugLog("freepost payload prepared", {
      creditorName,
      accountNumber,
      debtId: (debt as { id?: unknown })?.id,
      hasAddress: Boolean(freepostAddress),
      hasNotes: Boolean(freepostNotes),
    });

    // Only update if we find a record by debtId AND the existing record's debtId matches the new debt's id
    // This prevents updating orphaned records that might reference deleted debts
    const byId = existing.freepostByDebtId.get(debt.id);
    const existingDebtId = byId
      ? coerceNumericId((byId as unknown as { debtId?: unknown })?.debtId)
      : null;

    // FIRST CHECK: If the existing share record has already been shared, do NOT touch it.
    // Once a share record has submitDate or lastSharedDate, it is immutable from debt updates.
    // Neither update nor create a new row - just skip entirely.
    if (byId && hasBeenShared(byId)) {
      return {
        success: true,
        data: null,
        message: "Freepost sharing record already shared, skipping",
        timestamp: new Date().toISOString(),
      };
    }

    if (
      byId &&
      typeof (byId as { id?: number }).id === "number" &&
      existingDebtId === debt.id
    ) {
      // Record exists but hasn't been shared yet
      // Check if company (companyName) or reference (referenceNumber) changed
      const existingCompanyName = (byId as FreePostDataResponse & { companyName?: string }).companyName ?? "";
      const existingReferenceNumber = (byId as FreePostDataResponse).referenceNumber ?? "";
      const companyChanged = normalize(existingCompanyName) !== normalize(creditorName);
      const referenceChanged = normalize(existingReferenceNumber) !== normalize(accountNumber);

      if (companyChanged || referenceChanged) {
        // Company or reference changed - fall through to create a new share record
        // (the old unshared record will remain orphaned or be cleaned up separately)
      } else {
        // Company and reference same - safe to update with PATCH
        return DebtsService.updateFreePostSharing(
          { ...freepostPayload, id: (byId as { id: number }).id },
          { skipErrorCapture: true }
        );
      }
    }

    // Check if sharing record exists by key (creditor/account number) but debtId doesn't match.
    // As with digital sharing, only apply this duplicate guard for non profile-managed debts;
    // profile-managed debts are allowed to create a separate sharing row even when another
    // debt already has the same company/reference combination.
    const byKey = existing.freepostByKey.get(
      buildFreepostKey(creditorName, accountNumber)
    );
    if (byKey) {
      // If the existing record by key is already shared, skip - don't link new debt to shared record
      if (hasBeenShared(byKey)) {
        return {
          success: true,
          data: null,
          message: "Freepost sharing record already shared (by key), skipping",
          timestamp: new Date().toISOString(),
        };
      }
      const keyDebtId = (byKey as unknown as { debtId?: number })?.debtId;
      if (!isProfileManagedDebt && keyDebtId !== debt.id) {
        debugLog("skip: freepost duplicate for different debt", {
          creditorName,
          accountNumber,
          debtId: (debt as { id?: unknown })?.id,
          existingDebtId: keyDebtId ?? null,
        });
        logSkipSharing(
          "Freepost sharing exists for another debt (duplicate key), skipping",
          {
            customerId,
            debtId: (debt as { id?: unknown })?.id,
            existingDebtId: keyDebtId ?? null,
            companyName: creditorName,
            referenceNumber: accountNumber,
            profileManaged: isProfileManagedDebt,
          }
        );
        return {
          success: true,
          data: null,
          message:
            "Freepost sharing record exists for different debt, skipping",
          timestamp: new Date().toISOString(),
        };
      }
    }

    // If no record found by debtId or debtId doesn't match, create a new one (even if byKey exists, as it might be orphaned)
    try {
      return await DebtsService.createFreePostSharing(freepostPayload, {
        skipErrorCapture: true,
      });
    } catch (createError: any) {
      const errorMessage =
        createError?.response?.data?.errorMessage ?? createError?.message ?? "";
      if (errorMessage.includes("already exists")) {
        logSkipSharing("Freepost sharing already exists (API says exists)", {
          customerId,
          debtId: (debt as { id?: unknown })?.id,
          companyName: creditorName,
          referenceNumber: accountNumber,
        });
        return {
          success: true,
          data: null,
          message: "Freepost sharing record already exists, skipping",
          timestamp: new Date().toISOString(),
        };
      }
      throw createError;
    }
  },
};

export default SharingOrchestrator;
